import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Contact, UserPlus } from "lucide-react";

function PartnerTable({ kind, isAdmin }) {
  const invite = async (r) => {
    if (!window.confirm(`Kirim undangan Portal Customer ke ${r.email}?`)) return;
    try { const { data } = await api.post("/portal/invite", { email: r.email, name: r.name, company: r.company || "" });
      toast.success(data.email_sent ? `Undangan terkirim ke ${r.email}` : "Akun dibuat, tetapi email belum terkirim (cek pengaturan email)"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal mengundang"); }
  };
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = { name: "", company: "", email: "", phone: "", address: "", category: "", notes: "", website: "", price_list: "" };
  const [form, setForm] = useState(blank);
  const [edit, setEdit] = useState(null);
  const isSup = kind === "suppliers";

  const load = () => api.get(`/${kind}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nama wajib diisi"); return; }
    try {
      if (edit) await api.put(`/${kind}/${edit.id}`, form);
      else await api.post(`/${kind}`, form);
      toast.success("Data disimpan"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/${kind}/${id}`); toast.success("Data dihapus"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button data-testid={`add-${kind}-btn`} onClick={() => { setEdit(null); setForm(blank); setOpen(true); }} className="rounded-sm">
          <Plus className="w-4 h-4 mr-1" /> Tambah {isSup ? "Supplier" : "Customer"}
        </Button>
      </div>
      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary">
            <tr className="text-left overline text-muted-foreground">
              <th className="px-4 py-3">Nama</th><th className="px-4 py-3">Perusahaan</th>
              {isSup && <th className="px-4 py-3">Kategori</th>}
              <th className="px-4 py-3">Kontak</th><th className="px-4 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && (
              <tr><td colSpan={isSup ? 5 : 4} className="px-4 py-10 text-center text-muted-foreground">
                <Contact className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada data.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-secondary/40" data-testid={`${kind}-row-${r.id}`}>
                <td className="px-4 py-3 font-medium">{r.name}</td>
                <td className="px-4 py-3">{r.company || "-"}</td>
                {isSup && <td className="px-4 py-3">{r.category || "-"}</td>}
                <td className="px-4 py-3 text-xs text-muted-foreground">{r.email}{r.email && r.phone ? " · " : ""}{r.phone}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => { setEdit(r); setForm({ ...blank, ...r }); setOpen(true); }} className="text-muted-foreground hover:text-primary mr-3" data-testid={`edit-${kind}-${r.id}`}><Pencil className="w-4 h-4" /></button>
                  {isAdmin && kind === "customers" && r.email && <button onClick={() => invite(r)} title="Undang ke Portal Customer" className="text-muted-foreground hover:text-primary mr-3" data-testid={`invite-${r.id}`}><UserPlus className="w-4 h-4" /></button>}
                  {isAdmin && <button onClick={() => del(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-${kind}-${r.id}`}><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{edit ? "Edit" : "Tambah"} {isSup ? "Supplier" : "Customer"}</DialogTitle>
            <DialogDescription>{isSup ? "Data distributor mitra dipakai Sinta (Procurement) saat mencari harga." : "Data kontak customer."}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="overline text-muted-foreground">Nama</Label>
                <Input data-testid={`${kind}-name-input`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
              <div><Label className="overline text-muted-foreground">Perusahaan</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
              <div><Label className="overline text-muted-foreground">Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
              <div><Label className="overline text-muted-foreground">Telepon</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
            </div>
            {isSup && <div><Label className="overline text-muted-foreground">Kategori (mis. Laptop, Networking)</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>}
            {isSup && <div><Label className="overline text-muted-foreground">Website Distributor (dipakai Sinta mencari harga)</Label>
              <Input data-testid="suppliers-website-input" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="mis. www.distributor.co.id" className="mt-1 rounded-sm bg-secondary" /></div>}
            {isSup && <div><Label className="overline text-muted-foreground">Daftar Harga Distributor (satu baris per produk)</Label>
              <Textarea data-testid="suppliers-pricelist-input" value={form.price_list} onChange={(e) => setForm({ ...form, price_list: e.target.value })} placeholder={"Lenovo ThinkPad E14 i5 = 11.500.000\nCisco CBS350-24T = 7.200.000"} className="mt-1 rounded-sm bg-secondary min-h-[100px] font-mono text-xs" /></div>}
            <div><Label className="overline text-muted-foreground">Alamat</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
            <div><Label className="overline text-muted-foreground">Catatan</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-sm bg-secondary" /></div>
          </div>
          <DialogFooter><Button data-testid={`save-${kind}-btn`} onClick={save} className="rounded-sm">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Partners({ kind = "customers" }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isCust = kind === "customers";
  return (
    <div data-testid={`${kind}-page`}>
      <PageHeader overline="Mitra Kerja Sama" title={isCust ? "Customer" : "Supplier"} />
      <div className="p-8">
        <PartnerTable key={kind} kind={kind} isAdmin={isAdmin} />
      </div>
    </div>
  );
}
